import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/vigorbuy-data';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dreamy%20artistic%20floating%20fashion%20items%20in%20soft%20gradient%20background%2C%20ethereal%20cream%20beige%20and%20golden%20tones%2C%20editorial%20concept%20photography%2C%20elegant%20minimal%20aesthetic%20with%20gentle%20light%20rays%20and%20bokeh%2C%20airy%20atmospheric%20composition&width=1800&height=900&seq=hero-bg-v3&orientation=landscape&nocache=true"
          alt="VigorBuy Spreadsheet product discovery"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
          VigorBuy Spreadsheet 2026
        </h1>
        <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed">
          The Ultimate Product Discovery & Shopping Research Guide. Explore updated VigorBuy Spreadsheet collections, product categories, QC information, and expert buying guides designed to help you find better products faster.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
          >
            Explore VigorBuy Spreadsheet
            <i className="ri-arrow-right-line"></i>
          </a>
          <Link
            to="/vigorbuy-spreadsheet-guide"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-white/20 backdrop-blur-sm text-white font-semibold text-base border border-white/30 hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer"
          >
            View Buying Guides
            <i className="ri-book-open-line"></i>
          </Link>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 text-white/70 text-xs md:text-sm">
          <span className="flex items-center gap-1.5"><i className="ri-checkbox-circle-line text-accent-400"></i> Updated Resources</span>
          <span className="flex items-center gap-1.5"><i className="ri-checkbox-circle-line text-accent-400"></i> Category Comparison</span>
          <span className="flex items-center gap-1.5"><i className="ri-checkbox-circle-line text-accent-400"></i> QC Research Guide</span>
          <span className="flex items-center gap-1.5"><i className="ri-checkbox-circle-line text-accent-400"></i> Beginner Friendly</span>
        </div>

        <div className="mt-10 relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white/90 backdrop-blur-sm text-foreground-800 font-medium text-sm shadow-lg hover:bg-white transition-colors cursor-pointer"
          >
            Browse Product Categories
            <i className={`text-sm transition-transform ${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </button>

          {dropdownOpen && (
            <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-72 md:w-80 bg-background-50 rounded-xl shadow-xl border border-background-200/70 py-2 z-20 max-h-80 overflow-y-auto">
              {categories.map((cat, idx) => (
                <a
                  key={idx}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                  {cat.name}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}